import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, User, KeyRound } from "lucide-react";
import { MyHours } from "@/components/MyHours";
import { PersonalCalendar } from "@/components/PersonalCalendar";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/auth/profile", { name, email, phone });
      setUser(data);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("La nuova password e la conferma non coincidono");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="profile-back" onClick={() => navigate("/")} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="font-display font-bold">Il mio profilo</p>
            <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Il mio profilo</h1>

        <form onSubmit={saveProfile} data-testid="profile-form" className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><User className="h-4 w-4 text-[#D32F2F]" /> Dati personali</div>
          <div className="space-y-2">
            <Label>Nome e cognome</Label>
            <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" data-testid="profile-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Telefono <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
            <Input type="tel" data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="333 1234567" />
          </div>
          {user.qualifications?.length > 0 && (
            <div className="space-y-2">
              <Label>Le mie qualifiche</Label>
              <div className="flex flex-wrap gap-1.5">
                {user.qualifications.map((q) => <Badge key={q} variant="secondary" className="text-xs">{q}</Badge>)}
              </div>
              <p className="text-xs text-muted-foreground">Le qualifiche sono gestite dall'amministratore del comitato.</p>
            </div>
          )}
          <Button type="submit" data-testid="profile-save" disabled={savingProfile} className="rounded-full font-semibold">
            {savingProfile ? "Salvataggio…" : "Salva modifiche"}
          </Button>
        </form>

        <form onSubmit={savePassword} data-testid="password-form" className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><KeyRound className="h-4 w-4 text-[#D32F2F]" /> Cambia password</div>
          <div className="space-y-2">
            <Label>Password attuale</Label>
            <Input type="password" data-testid="password-current" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Nuova password</Label>
            <Input type="password" data-testid="password-new" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} />
          </div>
          <div className="space-y-2">
            <Label>Conferma nuova password</Label>
            <Input type="password" data-testid="password-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} />
          </div>
          <Button type="submit" data-testid="password-save" disabled={savingPwd} className="rounded-full font-semibold">
            {savingPwd ? "Salvataggio…" : "Aggiorna password"}
          </Button>
        </form>
        <PersonalCalendar />
        {user.role === "coordinatore" && <PersonalCalendar variant="committee" />}
        <MyHours />
      </main>
    </div>
  );
}
